<template>
  <div class="trigger-scheduled-editor">
    <!-- Node Intro -->
    <div class="node-intro">
      <div class="node-icon">
        <svg preserveAspectRatio="none" width="100%" height="100%" overflow="visible" style="display: block;" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.99167 1.66667C5.39167 1.66667 1.66667 5.4 1.66667 10C1.66667 14.6 5.39167 18.3333 9.99167 18.3333C14.6 18.3333 18.3333 14.6 18.3333 10C18.3333 5.4 14.6 1.66667 9.99167 1.66667ZM10 16.6667C6.31667 16.6667 3.33333 13.6833 3.33333 10C3.33333 6.31667 6.31667 3.33333 10 3.33333C13.6833 3.33333 16.6667 6.31667 16.6667 10C16.6667 13.6833 13.6833 16.6667 10 16.6667Z" fill="#20a7c1"/>
          <path d="M10.4167 5.83333H9.16667V10.8333L13.5417 13.4583L14.1667 12.4333L10.4167 10.2083V5.83333Z" fill="#20a7c1"/>
        </svg>
      </div>
      <p class="node-description">
        <span class="node-title">Scheduled Trigger</span> runs on a scheduled time frequency (daily, weekly, monthly).
      </p>
    </div>

    <!-- Start At -->
    <div class="input-group">
      <div class="input-label">Start at <span class="required">(Required)</span></div>
      <div class="start-row">
        <el-date-picker
          v-model="form.startDate"
          type="date"
          placeholder="MM/DD/YYYY"
          format="MM/DD/YYYY"
          class="start-date-picker"
        />
        <div class="time-row">
          <span class="at-label">at</span>
          <input
            v-model="form.startHour"
            type="number"
            min="1"
            max="12"
            class="time-input"
            placeholder="HH"
          />
          <span class="colon">:</span>
          <input
            v-model="form.startMinute"
            type="number"
            min="0"
            max="59"
            class="time-input"
            placeholder="MM"
          />
          <SingleSelector
            v-model="form.startAmpm"
            :options="ampmOptions"
            class="ampm-sel"
          />
        </div>
      </div>
    </div>

    <!-- Repeat Checkbox -->
    <div class="input-group">
      <label class="checkbox-label">
        <input v-model="form.repeat" type="checkbox" class="checkbox" />
        <span>Repeat this {{ isProgram ? 'program' : 'workflow' }}</span>
      </label>
    </div>

    <!-- Repeat Options -->
    <template v-if="form.repeat">

      <!-- Repeat Type -->
      <div class="input-group">
        <div class="input-label">Repeat every <span class="required">(Required)</span></div>
        <SingleSelector
          v-model="form.repeatType"
          :options="repeatTypeOptions"
          placeholder="Select repeat frequency"
        />
      </div>

      <!-- every N min/hr -->
      <template v-if="form.repeatType === 'min_hr'">
        <div class="input-group">
          <div class="input-label">Interval <span class="required">(Required)</span></div>
          <div class="time-input-row">
            <div class="input-wrapper num-wrapper">
              <input v-model="form.repeatValue" type="number" min="1" class="wait-input" placeholder="N" />
            </div>
            <SingleSelector v-model="form.repeatUnit" :options="minHrOptions" class="unit-sel" />
          </div>
        </div>
      </template>

      <!-- every N days at HH:MM -->
      <template v-if="form.repeatType === 'days'">
        <div class="input-group">
          <div class="input-label">Every <span class="required">(Required)</span></div>
          <div class="time-input-row">
            <div class="input-wrapper num-wrapper">
              <input v-model="form.repeatDaysValue" type="number" min="1" class="wait-input" placeholder="N" />
            </div>
            <span class="row-text">day(s)</span>
          </div>
        </div>
        <div class="input-group">
          <div class="input-label">At time <span class="required">(Required)</span></div>
          <div class="time-input-row">
            <input v-model="form.repeatHour" type="number" min="1" max="12" class="time-input" placeholder="HH" />
            <span class="time-separator">:</span>
            <input v-model="form.repeatMinute" type="number" min="0" max="59" class="time-input" placeholder="MM" />
            <SingleSelector v-model="form.repeatAmpm" :options="ampmOptions" class="ampm-dropdown" />
          </div>
        </div>
      </template>

      <!-- every N weeks on weekday at HH:MM -->
      <template v-if="form.repeatType === 'weeks'">
        <div class="input-group">
          <div class="input-label">Every <span class="required">(Required)</span></div>
          <div class="time-input-row">
            <div class="input-wrapper num-wrapper">
              <input v-model="form.repeatWeeksValue" type="number" min="1" class="wait-input" placeholder="N" />
            </div>
            <span class="row-text">week(s)</span>
          </div>
        </div>
        <div class="input-group">
          <div class="input-label">On weekday <span class="required">(Required)</span></div>
          <SingleSelector v-model="form.repeatWeekday" :options="weekdayOptions" />
        </div>
        <div class="input-group">
          <div class="input-label">At time <span class="required">(Required)</span></div>
          <div class="time-input-row">
            <input v-model="form.repeatWeekHour" type="number" min="1" max="12" class="time-input" placeholder="HH" />
            <span class="time-separator">:</span>
            <input v-model="form.repeatWeekMinute" type="number" min="0" max="59" class="time-input" placeholder="MM" />
            <SingleSelector v-model="form.repeatWeekAmpm" :options="ampmOptions" class="ampm-dropdown" />
          </div>
        </div>
      </template>

      <!-- last day of month at HH:MM -->
      <template v-if="form.repeatType === 'last_day'">
        <div class="input-group">
          <div class="input-label">At time <span class="required">(Required)</span></div>
          <div class="time-input-row">
            <input v-model="form.repeatLastHour" type="number" min="1" max="12" class="time-input" placeholder="HH" />
            <span class="time-separator">:</span>
            <input v-model="form.repeatLastMinute" type="number" min="0" max="59" class="time-input" placeholder="MM" />
            <SingleSelector v-model="form.repeatLastAmpm" :options="ampmOptions" class="ampm-dropdown" />
          </div>
        </div>
      </template>

      <!-- first day of month at HH:MM -->
      <template v-if="form.repeatType === 'first_day'">
        <div class="input-group">
          <div class="input-label">At time <span class="required">(Required)</span></div>
          <div class="time-input-row">
            <input v-model="form.repeatFirstHour" type="number" min="1" max="12" class="time-input" placeholder="HH" />
            <span class="time-separator">:</span>
            <input v-model="form.repeatFirstMinute" type="number" min="0" max="59" class="time-input" placeholder="MM" />
            <SingleSelector v-model="form.repeatFirstAmpm" :options="ampmOptions" class="ampm-dropdown" />
          </div>
        </div>
      </template>

      <!-- End Condition -->
      <div class="input-group">
        <div class="input-label">End condition</div>
        <SingleSelector
          v-model="form.endCondition"
          :options="endConditionOptions"
          placeholder="Select end condition"
        />
      </div>

      <!-- Ends at date/time -->
      <template v-if="form.endCondition === 'at_date'">
        <div class="input-group">
          <div class="input-label">End date <span class="required">(Required)</span></div>
          <el-date-picker
            v-model="form.endDate"
            type="date"
            placeholder="MM/DD/YYYY"
            format="MM/DD/YYYY"
            class="end-date-picker"
          />
        </div>
        <div class="input-group">
          <div class="input-label">End time <span class="required">(Required)</span></div>
          <div class="time-input-row">
            <input v-model="form.endHour" type="number" min="1" max="12" class="time-input" placeholder="HH" />
            <span class="time-separator">:</span>
            <input v-model="form.endMinute" type="number" min="0" max="59" class="time-input" placeholder="MM" />
            <SingleSelector v-model="form.endAmpm" :options="ampmOptions" class="ampm-dropdown" />
          </div>
        </div>
      </template>

      <!-- Ends after N runs -->
      <template v-if="form.endCondition === 'after_runs'">
        <div class="input-group">
          <div class="input-label">After <span class="required">(Required)</span></div>
          <div class="time-input-row">
            <div class="input-wrapper num-wrapper">
              <input v-model="form.endAfterRuns" type="number" min="1" class="wait-input" placeholder="N" />
            </div>
            <span class="row-text">run(s)</span>
          </div>
        </div>
      </template>

      <!-- Upcoming Runs Info Box -->
      <div class="info-box">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0;margin-top:1px">
          <path d="M9 5H11V7H9V5ZM9 9H11V15H9V9ZM10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18Z" fill="#004879"/>
        </svg>
        <div class="info-box-content">
          <button class="upcoming-btn" @click="toggleUpcomingRuns">
            {{ showUpcomingRuns ? 'Hide upcoming runs' : 'View up to 10 upcoming runs' }}
          </button>
          <div v-if="showUpcomingRuns && upcomingRuns.length > 0" class="upcoming-list">
            <div v-for="(run, idx) in upcomingRuns" :key="idx" class="upcoming-item">
              {{ idx + 1 }}. {{ formatRunDate(run) }}
            </div>
          </div>
          <div v-else-if="showUpcomingRuns" class="no-runs-text">
            Unable to calculate — please complete the schedule configuration.
          </div>
        </div>
      </div>

    </template>

    <!-- Timezone -->
    <div class="input-group">
      <div class="input-label">Time Zone</div>
      <SingleSelector
        v-model="form.timezone"
        :options="timezoneOptions"
        placeholder="Select timezone"
        class="tz-selector"
      />
    </div>

    <!-- Save Configuration -->
    <SaveConfigure
      :disabled="!isValid"
      @save-configuration="handleSave"
    />
  </div>
</template>

<script>
import SaveConfigure from '@/components/workflow/SaveConfigure.vue';
import SingleSelector from '@/NewLibrary/SingleSelector.vue';

export default {
  name: 'TriggerScheduledEditor',
  components: { SaveConfigure, SingleSelector },
  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    isProgram: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const raw = (this.data && this.data.savedData) ? this.data.savedData : (this.data || {});

    // Deserialise from backend ScheduledTriggerConfigV2 format into UI fields
    const tp = raw.timePointConfig || {};
    const ec = raw.endCondition || {};

    // Derive repeatType from timePointConfig.type
    let repeatType = 'days';
    if (tp.type === 'every_n_minutes_or_hours') repeatType = 'min_hr';
    else if (tp.type === 'every_n_days_at_time') repeatType = 'days';
    else if (tp.type === 'every_n_weeks_on_weekday_at_time') repeatType = 'weeks';
    else if (tp.type === 'month_boundary' && tp.boundary === 'last_day') repeatType = 'last_day';
    else if (tp.type === 'month_boundary' && tp.boundary === 'first_day') repeatType = 'first_day';

    // Derive end condition UI fields
    let endConditionVal = 'never';
    let endDate = null;
    let endHour = '';
    let endMinute = '';
    let endAmpm = 'AM';
    let endAfterRuns = '';
    if (ec.type === 'end_at_datetime') {
      endConditionVal = 'at_date';
      if (ec.endAt) {
        const dt = new Date(ec.endAt);
        endDate = dt;
        const h = dt.getHours();
        endAmpm = h >= 12 ? 'PM' : 'AM';
        endHour = String(h % 12 || 12);
        endMinute = String(dt.getMinutes()).padStart(2, '0');
      }
    } else if (ec.type === 'end_after_n_runs') {
      endConditionVal = 'after_runs';
      endAfterRuns = ec.maxRuns || '';
    }

    // Whether repeat is enabled (has a timePointConfig)
    const repeat = !!(raw.mode && raw.timePointConfig);

    return {
      showUpcomingRuns: false,
      form: {
        startDate: raw.startDate || null,
        startHour: raw.startHour || '',
        startMinute: raw.startMinute !== undefined ? raw.startMinute : '',
        startAmpm: raw.startAmpm || 'AM',

        repeat,
        repeatType,

        // every_n_minutes_or_hours
        repeatValue: tp.every || '',
        repeatUnit: tp.unit || 'minutes',

        // every_n_days_at_time
        repeatDaysValue: tp.every || '',
        repeatHour: tp.time ? tp.time.split(':')[0] : '',
        repeatMinute: tp.time ? (tp.time.split(':')[1] || '') : '',
        repeatAmpm: tp.meridiem || 'AM',

        // every_n_weeks_on_weekday_at_time
        repeatWeeksValue: tp.every || '',
        repeatWeekday: tp.weekday || 'Mon',
        repeatWeekHour: tp.time ? tp.time.split(':')[0] : '',
        repeatWeekMinute: tp.time ? (tp.time.split(':')[1] || '') : '',
        repeatWeekAmpm: tp.meridiem || 'AM',

        // month_boundary (last_day / first_day) — no time in backend schema
        repeatLastHour: '',
        repeatLastMinute: '',
        repeatLastAmpm: 'AM',
        repeatFirstHour: '',
        repeatFirstMinute: '',
        repeatFirstAmpm: 'AM',

        endCondition: endConditionVal,
        endDate,
        endHour,
        endMinute,
        endAmpm,
        endAfterRuns,

        timezone: raw.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
      },
      ampmOptions: [
        { label: 'AM', value: 'AM' },
        { label: 'PM', value: 'PM' }
      ],
      minHrOptions: [
        { label: 'minutes', value: 'minutes' },
        { label: 'hours', value: 'hours' }
      ],
      weekdayOptions: [
        { label: 'Mon', value: 'Mon' },
        { label: 'Tue', value: 'Tue' },
        { label: 'Wed', value: 'Wed' },
        { label: 'Thu', value: 'Thu' },
        { label: 'Fri', value: 'Fri' },
        { label: 'Sat', value: 'Sat' },
        { label: 'Sun', value: 'Sun' }
      ],
      repeatTypeOptions: [
        { label: 'Every N minutes/hours', value: 'min_hr' },
        { label: 'Every N days', value: 'days' },
        { label: 'Every N weeks', value: 'weeks' },
        { label: 'Last day of the month', value: 'last_day' },
        { label: 'First day of the month', value: 'first_day' }
      ],
      endConditionOptions: [
        { label: 'Never ends', value: 'never' },
        { label: 'Ends at a specific date/time', value: 'at_date' },
        { label: 'Ends after N runs', value: 'after_runs' }
      ],
      timezoneOptions: [
        { label: 'America/Los Angeles (PT)', value: 'America/Los_Angeles' },
        { label: 'America/Denver (MT)', value: 'America/Denver' },
        { label: 'America/Chicago (CT)', value: 'America/Chicago' },
        { label: 'America/New_York (ET)', value: 'America/New_York' },
        { label: 'America/Phoenix (AZ)', value: 'America/Phoenix' },
        { label: 'America/Anchorage (AKT)', value: 'America/Anchorage' },
        { label: 'Pacific/Honolulu (HT)', value: 'Pacific/Honolulu' },
        { label: 'UTC', value: 'UTC' },
        { label: 'Europe/London (GMT)', value: 'Europe/London' },
        { label: 'Asia/Tokyo (JST)', value: 'Asia/Tokyo' }
      ]
    };
  },
  computed: {
    startDateTime() {
      if (!this.form.startDate || !this.form.startHour || this.form.startMinute === '') return null;
      const d = new Date(this.form.startDate);
      let h = parseInt(this.form.startHour);
      const m = parseInt(this.form.startMinute);
      if (this.form.startAmpm === 'PM' && h !== 12) h += 12;
      if (this.form.startAmpm === 'AM' && h === 12) h = 0;
      d.setHours(h, m, 0, 0);
      return d;
    },

    isValid() {
      if (!this.startDateTime) return false;
      if (!this.form.repeat) return true;
      const { repeatType } = this.form;
      if (repeatType === 'min_hr') return !!(this.form.repeatValue && this.form.repeatValue > 0);
      if (repeatType === 'days') {
        return !!(this.form.repeatDaysValue && this.form.repeatDaysValue > 0 &&
          this.form.repeatHour && this.form.repeatMinute !== '');
      }
      if (repeatType === 'weeks') {
        return !!(this.form.repeatWeeksValue && this.form.repeatWeeksValue > 0 &&
          this.form.repeatWeekday && this.form.repeatWeekHour && this.form.repeatWeekMinute !== '');
      }
      if (repeatType === 'last_day') return !!(this.form.repeatLastHour && this.form.repeatLastMinute !== '');
      if (repeatType === 'first_day') return !!(this.form.repeatFirstHour && this.form.repeatFirstMinute !== '');
      return false;
    },

    endDateTimeObj() {
      if (this.form.endCondition !== 'at_date' || !this.form.endDate || !this.form.endHour) return null;
      const d = new Date(this.form.endDate);
      let h = parseInt(this.form.endHour);
      const m = parseInt(this.form.endMinute) || 0;
      if (this.form.endAmpm === 'PM' && h !== 12) h += 12;
      if (this.form.endAmpm === 'AM' && h === 12) h = 0;
      d.setHours(h, m, 0, 0);
      return d;
    },

    upcomingRuns() {
      if (!this.isValid || !this.form.repeat) return [];
      const runs = [];
      let current = new Date(this.startDateTime);
      const endDate = this.endDateTimeObj;
      for (let i = 0; i < 10; i++) {
        if (endDate && current > endDate) break;
        if (this.form.endCondition === 'after_runs' && i >= parseInt(this.form.endAfterRuns)) break;
        runs.push(new Date(current));
        const next = this.getNextRun(current);
        if (!next || next <= current) break;
        current = next;
      }
      return runs;
    }
  },
  methods: {
    getRepeatTimeHMS(type) {
      const map = {
        days: [this.form.repeatHour, this.form.repeatMinute, this.form.repeatAmpm],
        weeks: [this.form.repeatWeekHour, this.form.repeatWeekMinute, this.form.repeatWeekAmpm],
        last_day: [this.form.repeatLastHour, this.form.repeatLastMinute, this.form.repeatLastAmpm],
        first_day: [this.form.repeatFirstHour, this.form.repeatFirstMinute, this.form.repeatFirstAmpm]
      };
      const [h, m, ap] = map[type] || [null, null, 'AM'];
      if (!h) return null;
      let hour = parseInt(h);
      const min = parseInt(m) || 0;
      if (ap === 'PM' && hour !== 12) hour += 12;
      if (ap === 'AM' && hour === 12) hour = 0;
      return { hour, min };
    },

    getNextRun(from) {
      const { repeatType } = this.form;
      const d = new Date(from);
      if (repeatType === 'min_hr') {
        const val = parseInt(this.form.repeatValue) || 1;
        if (this.form.repeatUnit === 'minutes') d.setMinutes(d.getMinutes() + val);
        else d.setHours(d.getHours() + val);
        return d;
      }
      if (repeatType === 'days') {
        const t = this.getRepeatTimeHMS('days');
        if (!t) return null;
        d.setDate(d.getDate() + (parseInt(this.form.repeatDaysValue) || 1));
        d.setHours(t.hour, t.min, 0, 0);
        return d;
      }
      if (repeatType === 'weeks') {
        const t = this.getRepeatTimeHMS('weeks');
        if (!t) return null;
        d.setDate(d.getDate() + (parseInt(this.form.repeatWeeksValue) || 1) * 7);
        d.setHours(t.hour, t.min, 0, 0);
        return d;
      }
      if (repeatType === 'last_day') {
        const t = this.getRepeatTimeHMS('last_day');
        if (!t) return null;
        d.setMonth(d.getMonth() + 2, 0);
        d.setHours(t.hour, t.min, 0, 0);
        return d;
      }
      if (repeatType === 'first_day') {
        const t = this.getRepeatTimeHMS('first_day');
        if (!t) return null;
        d.setMonth(d.getMonth() + 1, 1);
        d.setHours(t.hour, t.min, 0, 0);
        return d;
      }
      return null;
    },

    formatRunDate(date) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const day = days[date.getDay()];
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yyyy = date.getFullYear();
      let h = date.getHours();
      const min = String(date.getMinutes()).padStart(2, '0');
      const ap = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${mm}/${dd}/${yyyy} (${day}), ${h}:${min} ${ap}`;
    },

    toggleUpcomingRuns() {
      this.showUpcomingRuns = !this.showUpcomingRuns;
    },

    toBackendConfig() {
      const f = this.form;
      const config = { configured: true };

      if (!f.repeat) {
        config.mode = 'duration';
        config.endCondition = { type: 'never' };
        return config;
      }

      config.mode = 'time_point';

      if (f.repeatType === 'min_hr') {
        config.timePointConfig = {
          type: 'every_n_minutes_or_hours',
          every: Number(f.repeatValue),
          unit: f.repeatUnit  // 'minutes' or 'hours'
        };
      } else if (f.repeatType === 'days') {
        config.timePointConfig = {
          type: 'every_n_days_at_time',
          every: Number(f.repeatDaysValue),
          time: `${f.repeatHour}:${String(f.repeatMinute).padStart(2, '0')}`,
          meridiem: f.repeatAmpm
        };
      } else if (f.repeatType === 'weeks') {
        config.timePointConfig = {
          type: 'every_n_weeks_on_weekday_at_time',
          every: Number(f.repeatWeeksValue),
          weekday: f.repeatWeekday,
          time: `${f.repeatWeekHour}:${String(f.repeatWeekMinute).padStart(2, '0')}`,
          meridiem: f.repeatWeekAmpm
        };
      } else if (f.repeatType === 'last_day') {
        config.timePointConfig = { type: 'month_boundary', boundary: 'last_day' };
      } else if (f.repeatType === 'first_day') {
        config.timePointConfig = { type: 'month_boundary', boundary: 'first_day' };
      }

      // End condition
      if (f.endCondition === 'never') {
        config.endCondition = { type: 'never' };
      } else if (f.endCondition === 'after_runs') {
        config.endCondition = { type: 'end_after_n_runs', maxRuns: Number(f.endAfterRuns) };
      } else if (f.endCondition === 'at_date' && f.endDate) {
        const d = new Date(f.endDate);
        let h = Number(f.endHour);
        const m = Number(f.endMinute) || 0;
        if (f.endAmpm === 'PM' && h !== 12) h += 12;
        if (f.endAmpm === 'AM' && h === 12) h = 0;
        d.setHours(h, m, 0, 0);
        config.endCondition = { type: 'end_at_datetime', endAt: d.toISOString() };
      }

      return config;
    },

    handleSave() {
      if (!this.isValid) return;
      this.$emit('save', this.toBackendConfig());
    }
  }
};
</script>

<style scoped>
.trigger-scheduled-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

/* Node intro */
.node-intro {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 8px 16px;
  background: #E5F9EF;
  border-radius: 5px;
}

.node-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.node-description {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #20A7C1;
  margin: 0;
}

.node-title { font-weight: 700; }

/* Input groups (matches LogicWaitEditor pattern) */
.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.input-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
}

.required { color: #D10000; }

/* Start row */
.start-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.time-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.start-date-picker { width: 150px; }

:deep(.start-date-picker .el-input__wrapper) {
  height: 36px;
  border: 1px solid #C9C9C9 !important;
  box-shadow: none !important;
  border-radius: 4px;
}

.end-date-picker { width: 100%; }

:deep(.end-date-picker .el-input__wrapper) {
  height: 40px;
  border: 1px solid #C9C9C9 !important;
  box-shadow: none !important;
  border-radius: 4px;
}

.at-label,
.colon,
.inline-text {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
}

.time-input {
  width: 60px;
  height: 40px;
  padding: 8px 12px;
  background: #FFFFFF;
  border: 1px solid #C9C9C9;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
  text-align: center;
  outline: none;
}

.time-input::placeholder { color: #999; }
.time-input::-webkit-inner-spin-button,
.time-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
.time-input[type=number] { -moz-appearance: textfield; appearance: textfield; }
.time-input:focus { border-color: #004879; }

.ampm-sel { width: 68px; }

/* Time input row (LogicWaitEditor pattern) */
.time-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.time-separator {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #333;
}

.ampm-dropdown {
  width: 80px;
  flex-shrink: 0;
}

.unit-sel {
  width: 90px;
  flex-shrink: 0;
}

.num-wrapper {
  width: 80px;
  flex-shrink: 0;
}

.row-text {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
}

.input-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  padding: 8px 12px;
  background: #FFFFFF;
  border: 1px solid #C9C9C9;
  border-radius: 4px;
}

.wait-input {
  flex: 1;
  width: 100%;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
  border: none;
  outline: none;
  background: transparent;
}

.wait-input::placeholder { color: #999; }
.wait-input::-webkit-inner-spin-button,
.wait-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
.wait-input[type=number] { -moz-appearance: textfield; appearance: textfield; }

/* Checkbox */

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #004879;
}

.tz-selector { width: 100%; }

/* SingleSelector height overrides */
:deep(.new-libary-single-selector .el-select__wrapper) {
  height: 40px;
  border: 1px solid #C9C9C9 !important;
  box-shadow: none !important;
  border-radius: 4px;
}

:deep(.new-libary-single-selector .el-select__wrapper.is-focused) {
  border-color: #004879 !important;
}

:deep(.new-libary-single-selector .el-select__placeholder),
:deep(.new-libary-single-selector .el-select__selected-item) {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #333;
}

/* Info box */
.info-box {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  background: #E3F2FD;
  border: 1px solid #90CAF9;
  border-radius: 4px;
}

.info-box-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.upcoming-btn {
  background: transparent;
  border: none;
  padding: 0;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #004879;
  text-decoration: underline;
  cursor: pointer;
  text-align: left;
}

.upcoming-btn:hover { opacity: 0.8; }

.upcoming-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.upcoming-item {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #333;
}

.no-runs-text {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #999;
  font-style: italic;
}
</style>
